import React, { useCallback, useMemo, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '@assets/icons/delete.svg';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Link from '@material-ui/core/Link';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';

import {
  WildCardDomainsCertGenerateLoading as certCreateLoadingSelector,
  WildCardDomainsCustomDomain as customDomainSelector,
  WildCardDomainsDeleteDomain as deletingDomainSelector,
  WildCardDomainsBindSslCertificate as bindInProgressSelector,
} from '@store/slices/wildcardDomains';
import {
  ThunkDeleteCustomDomain,
  ThunkBindSslCertificate,
  ThunkCreateSslCertificate,
} from '@store/slices/wildcardDomains/thunks';

import ActionFieldItem from '@common/ActionButtons/ActionFieldItem';
import ConfirmDialog from '@common/ConfirmDialog/ConfirmDialog';
import Timer from '@common/Timer/Timer';
import { COUNTDOWN_DURATION } from '../constants';

import useCountdown from '../../../../../hooks/useCountdown';

import useStyles from '../styles';

const CustomTenantDomain = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const customTenantDomain = useSelector(customDomainSelector);
  const deleteInProgress = useSelector(deletingDomainSelector);
  const certLoading = useSelector(certCreateLoadingSelector);
  const bindInProgress = useSelector(bindInProgressSelector);
  const { isActive, formattedTime } = useCountdown('ssl_countdown_finish_time');

  const domainInfo = useMemo(
    () => ({
      domain: customTenantDomain?.domain || '',
      ssl: customTenantDomain?.sslEnabled,
    }),
    [customTenantDomain],
  );

  const createCertificate = useCallback(() => {
    dispatch(ThunkCreateSslCertificate(domainInfo.domain))
      .unwrap()
      .then(() => {
        const readyTime = new Date().getTime() + COUNTDOWN_DURATION * 1000;
        localStorage.setItem('ssl_countdown_finish_time', readyTime.toString());
        window.dispatchEvent(new Event('storage'));
      });
  }, [dispatch, domainInfo]);

  const bindCertificate = useCallback(() => {
    dispatch(ThunkBindSslCertificate(domainInfo.domain)).then(() => {
      localStorage.removeItem('ssl_countdown_finish_time');
    });
  }, [dispatch, domainInfo]);

  const onDelete = useCallback(() => {
    dispatch(ThunkDeleteCustomDomain())
      .unwrap()
      .then(() => {
        setConfirmDialogOpen(false);
        localStorage.removeItem('ssl_created');
      });
  }, [dispatch]);

  const renderSSLStatus = () => {
    if (!customTenantDomain?.domain) {
      return null;
    }

    if (
      !domainInfo.ssl ||
      !customTenantDomain?.sslExists ||
      !customTenantDomain?.sslEnabled
    ) {
      return (
        <div className={classes.bindingPrompt}>
          <Typography variant="body1" className={classes.bindingText}>
            To finish setting up your custom domain, please click the Create the
            SSL certificate button, wait for a few minutes for Microsoft to
            finish propagating out that change, and then click the Bind the SSL
            certificate button. If you get an error when you click the Bind the
            SSL certificate button, please wait a few minutes and try again.
          </Typography>
          {isActive && (
            <>
              <Typography variant="body2" color="textSecondary">
                Please wait before binding the SSL certificate
              </Typography>
              <Timer
                storageKey="ssl_countdown_finish_time"
                formattedTime={formattedTime}
                className={classes.countdown}
              />
            </>
          )}
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              onClick={createCertificate}
              disabled={customTenantDomain?.sslExists || certLoading}
              className={classes.bindButton}
            >
              {certLoading
                ? 'Creating Certificate...'
                : 'Create the SSL certificate'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={bindCertificate}
              disabled={
                !customTenantDomain?.sslExists ||
                customTenantDomain?.sslEnabled ||
                isActive ||
                bindInProgress
              }
              className={classes.bindButton}
            >
              {bindInProgress
                ? 'Binding Certificate...'
                : 'Bind SSL Certificate'}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className={classes.customTenantDomainContainer}>
        {customTenantDomain?.domain && (
          <div className={classes.successMessage}>
            <CheckCircleIcon color="success" className={classes.checkIcon} />
            <Typography>
              Your domain has been successfully configured
            </Typography>
          </div>
        )}

        {customTenantDomain?.sslExists && (
          <div className={classes.successMessage}>
            <CheckCircleIcon color="success" className={classes.checkIcon} />
            <Typography>
              Your SSL certificate has been successfully created
            </Typography>
          </div>
        )}

        {customTenantDomain?.sslEnabled && (
          <div className={classes.successMessage}>
            <CheckCircleIcon color="success" className={classes.checkIcon} />
            <Typography>
              Your SSL certificate has been successfully bound
            </Typography>
          </div>
        )}

        {renderSSLStatus()}

        {customTenantDomain?.domain && (
          <div className={classes.customDomainName}>
            <Link
              href={`https://${domainInfo.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.domainLink}
            >
              {customTenantDomain.domain}
            </Link>
            <div>
              <ActionFieldItem
                handler={() => setConfirmDialogOpen(true)}
                toolTip="Delete domain"
                className={classes.deleteButton}
                disabled={certLoading || bindInProgress}
              >
                <img src={DeleteIcon} alt="delete" />
              </ActionFieldItem>
            </div>
          </div>
        )}

        {(certLoading || bindInProgress) && (
          <LinearProgress className={classes.loadingBar} />
        )}
      </div>

      <ConfirmDialog
        open={confirmDialogOpen}
        setOpen={() => setConfirmDialogOpen(true)}
        onAccept={onDelete}
        onCancel={() => setConfirmDialogOpen(false)}
        title="Delete Domain"
        content="Are you sure you would like to delete this domain? This action will also remove any associated certificate bindings."
        loading={deleteInProgress}
      />
    </>
  );
};

export default CustomTenantDomain;
